import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router'
import { hasPermission } from '@rapid-response/shared'
import { PageHeader } from '~/components/PageHeader'
import { TabNav, type TabItem } from '~/components/TabNav'
import { navNodeTarget, resolveActiveGuideNav, visibleGuideNav } from '~/lib/guide-nav'
import { useCurrentUser, usePermissionGuard } from '~/lib/current-user'
import { seo } from '~/utils/seo'

export const Route = createFileRoute('/guide')({
  head: () => ({
    meta: seo({ title: 'Guide', description: 'Learn how to use First Response.' }),
  }),
  component: GuideLayout,
})

// The guide section is gated by `guide:read` (granted per-role in the Roles
// editor; `admin` implies it). Within it, admin topics are additionally
// hidden from non-admins — documentation mirrors the sidebar.
function GuideLayout() {
  const allowed = usePermissionGuard('guide:read')
  const matches = useMatches()
  const { permissions } = useCurrentUser()
  const activePaths: string[] = matches.map((m) => m.fullPath)
  const active = resolveActiveGuideNav(activePaths)

  // Only surface topics the user can actually reach — admin docs stay hidden
  // from non-admins, mirroring the sidebar.
  const nav = visibleGuideNav((permission) => hasPermission(permissions, permission))
  const topTabs: TabItem[] = nav.map((node) => ({
    to: navNodeTarget(node),
    label: node.label,
    isActive: active?.node === node,
  }))
  const subTabs: TabItem[] = (active?.subTabs ?? []).map((tab) => ({
    to: tab.to,
    label: tab.label,
    isActive: activePaths.includes(tab.to),
  }))

  if (!allowed) {
    return <div className="text-muted-foreground text-sm">Checking permissions...</div>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="First Response Guide"
        description="Concepts, features, and admin — everything you need to use First Response."
      />
      <TabNav tabs={topTabs} ariaLabel="Guide sections" />
      {subTabs.length > 0 && <TabNav tabs={subTabs} ariaLabel="Guide subsections" />}
      <Outlet />
    </div>
  )
}
